/**
 * PromtrContext.tsx - React Context for Promptr
 * 
 * CHANGE HISTORY:
 * - 251025_1600 [pending] danielbeer: Initial creation
 * 
 * Provides Promptr functionality throughout React app
 */

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { PromtrManager } from './PromtrManager';
import { ConfirmDialog } from './components/ConfirmDialog';
import { AlertDialog } from './components/AlertDialog';
import { PromptDialog } from './components/PromptDialog';
import { ToastContainer, ToastItem } from './components/ToastContainer';
import {
  ConfirmConfig,
  AlertConfig,
  PromptConfig,
  ToastConfig,
  PromtrOptions
} from './types';

interface PromtrContextValue {
  confirm: (config: ConfirmConfig) => Promise<boolean>;
  alert: (config: AlertConfig) => Promise<void>;
  prompt: (config: PromptConfig) => Promise<string | null>;
  toast: (message: string, config?: Partial<ToastConfig>) => string;
  success: (message: string, duration?: number) => string;
  error: (message: string, duration?: number) => string;
  warning: (message: string, duration?: number) => string;
  info: (message: string, duration?: number) => string;
  dismissToast: (id: string) => void;
  dismissAllToasts: () => void;
}

const PromtrContext = createContext<PromtrContextValue | null>(null);

export interface PromtrProviderProps {
  children: ReactNode;
  options?: PromtrOptions;
}

/**
 * PromtrProvider - Provides Promptr functionality to React app
 * 
 * Wrap your app with this provider to use Promptr hooks
 */
export const PromtrProvider: React.FC<PromtrProviderProps> = ({ children, options = {} }) => {
  const [manager] = useState(() => new PromtrManager(options));
  
  // Dialog state
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    config: ConfirmConfig;
    resolve: ((value: boolean) => void) | null;
  }>({
    isOpen: false,
    config: { title: '', message: '' },
    resolve: null
  });

  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    config: AlertConfig;
    resolve: (() => void) | null;
  }>({
    isOpen: false,
    config: { title: '', message: '' },
    resolve: null
  });

  const [promptState, setPromptState] = useState<{
    isOpen: boolean;
    config: PromptConfig;
    resolve: ((value: string | null) => void) | null;
  }>({
    isOpen: false,
    config: { title: '', message: '' },
    resolve: null
  });

  // Toast state
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Confirm
  const confirm = useCallback((config: ConfirmConfig): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        config,
        resolve
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (confirmState.resolve) {
      confirmState.resolve(true);
    }
    setConfirmState({ isOpen: false, config: { title: '', message: '' }, resolve: null });
  }, [confirmState]);

  const handleConfirmCancel = useCallback(() => {
    if (confirmState.resolve) {
      confirmState.resolve(false);
    }
    setConfirmState({ isOpen: false, config: { title: '', message: '' }, resolve: null });
  }, [confirmState]);

  // Alert
  const alert = useCallback((config: AlertConfig): Promise<void> => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        config,
        resolve
      });
    });
  }, []);

  const handleAlertClose = useCallback(() => {
    if (alertState.resolve) {
      alertState.resolve();
    }
    setAlertState({ isOpen: false, config: { title: '', message: '' }, resolve: null });
  }, [alertState]);

  // Prompt
  const prompt = useCallback((config: PromptConfig): Promise<string | null> => {
    return new Promise((resolve) => {
      setPromptState({
        isOpen: true,
        config,
        resolve
      });
    });
  }, []);

  const handlePromptSubmit = useCallback((value: string) => {
    if (promptState.resolve) {
      promptState.resolve(value);
    }
    setPromptState({ isOpen: false, config: { title: '', message: '' }, resolve: null });
  }, [promptState]);

  const handlePromptCancel = useCallback(() => {
    if (promptState.resolve) {
      promptState.resolve(null);
    }
    setPromptState({ isOpen: false, config: { title: '', message: '' }, resolve: null });
  }, [promptState]);

  // Toast
  const toast = useCallback((message: string, config: Partial<ToastConfig> = {}): string => {
    const id = manager.toast(message, config);
    const toastItem: ToastItem = {
      id,
      message,
      type: config.type || 'info',
      duration: config.duration ?? manager['options'].defaultDuration,
      position: config.position || manager['options'].defaultPosition,
      action: config.action
    };
    setToasts((prev) => [...prev, toastItem]);
    return id;
  }, [manager]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    manager.dismissToast(id);
  }, [manager]);

  const dismissAllToasts = useCallback(() => {
    setToasts([]);
    manager.dismissAllToasts();
  }, [manager]);

  // Shorthand methods
  const success = useCallback((message: string, duration?: number) => {
    return toast(message, { type: 'success', duration });
  }, [toast]);

  const error = useCallback((message: string, duration?: number) => {
    return toast(message, { type: 'error', duration });
  }, [toast]);

  const warning = useCallback((message: string, duration?: number) => {
    return toast(message, { type: 'warning', duration });
  }, [toast]);

  const info = useCallback((message: string, duration?: number) => {
    return toast(message, { type: 'info', duration });
  }, [toast]);

  // Memoize context value to prevent infinite re-renders
  const value = useMemo<PromtrContextValue>(() => ({
    confirm,
    alert,
    prompt,
    toast,
    success,
    error,
    warning,
    info,
    dismissToast,
    dismissAllToasts
  }), [confirm, alert, prompt, toast, success, error, warning, info, dismissToast, dismissAllToasts]);

  return (
    <PromtrContext.Provider value={value}>
      {children}
      
      {/* Dialogs */}
      <ConfirmDialog
        {...confirmState.config}
        isOpen={confirmState.isOpen}
        onConfirm={handleConfirm}
        onCancel={handleConfirmCancel}
      />
      
      <AlertDialog
        {...alertState.config}
        isOpen={alertState.isOpen}
        onClose={handleAlertClose}
      />
      
      <PromptDialog
        {...promptState.config}
        isOpen={promptState.isOpen}
        onSubmit={handlePromptSubmit}
        onCancel={handlePromptCancel}
      />

      {/* Toasts */}
      <ToastContainer
        toasts={toasts}
        position={options.defaultPosition}
        onDismiss={dismissToast}
      />
    </PromtrContext.Provider>
  );
};

/**
 * usePromptr - Hook to access Promptr functionality
 * 
 * @returns PromtrContextValue
 */
export const usePromptr = (): PromtrContextValue => {
  const context = useContext(PromtrContext);
  if (!context) {
    throw new Error('usePromptr must be used within PromtrProvider');
  }
  return context;
};

/**
 * usePromtr - Alias for usePromptr (backward compatibility)
 * @deprecated Use usePromptr instead
 */
export const usePromtr = usePromptr;
